import pick from 'lodash/pick';
import {
  initiatePrivileged,
  initiateWalletPrivileged,
  PayPalApi,
  SharetribeApi,
  StripeApi,
  transitionPrivileged,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { isPrivileged, transitions } from '../../transactions/transactionProcessPurchase';
import { buildTitlePriceArray } from '../../util/genericHelpers';

const notifyTx = async txIdLike => {
  const txId =
    typeof txIdLike === 'string' ? txIdLike : txIdLike?.uuid || txIdLike?.id?.uuid || txIdLike;
  if (!txId) return null;

  try {
    return await SharetribeApi.createTxNotificationApi({ txId });
  } catch (e) {
    log.error(e, 'tx-notification-failed', { txId });
    return null;
  }
};

const withEmailDataInProtectedData = (orderParams = {}) => {
  const cartItems = orderParams?.cartItems || [];
  const emailData = buildTitlePriceArray(cartItems);

  return {
    ...orderParams,
    protectedData: {
      ...(orderParams?.protectedData || {}),
      emailData,
    },
  };
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const CREATE_PAYMENT_INTENT_REQUEST = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_REQUEST';
export const CREATE_PAYMENT_INTENT_SUCCESS = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_SUCCESS';
export const CREATE_PAYMENT_INTENT_ERROR = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  createPaymentIntentInProgress: false,
  createPaymentIntentError: null,
  walletPaymentIntent: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
      };
    }
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload);
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload);
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload);
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload);
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case CREATE_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        createPaymentIntentInProgress: true,
        createPaymentIntentError: null,
      };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return {
        ...state,
        createPaymentIntentInProgress: false,
        walletPaymentIntent: payload,
      };
    case CREATE_PAYMENT_INTENT_ERROR:
      return {
        ...state,
        createPaymentIntentInProgress: false,
        createPaymentIntentError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getPaymentIntentState = state => ({
  createPaymentIntentInProgress: state.CheckoutPage.createPaymentIntentInProgress,
  createPaymentIntentError: state.CheckoutPage.createPaymentIntentError,
  walletPaymentIntent: state.CheckoutPage.walletPaymentIntent,
});

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const createPaymentIntentRequest = () => ({
  type: CREATE_PAYMENT_INTENT_REQUEST,
});

export const createPaymentIntentSuccess = payload => ({
  type: CREATE_PAYMENT_INTENT_SUCCESS,
  payload,
});

export const createPaymentIntentError = error => ({
  type: CREATE_PAYMENT_INTENT_ERROR,
  payload: error,
  error: true,
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  const isTransition = !!transactionId;
  orderParams = withEmailDataInProtectedData(orderParams);
  const {
    deliveryMethod,
    quantity,
    bookingDates,
    cartItems,
    size,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(size ? { size } : {}),
    ...(cartItems ? { cartItems } : {}),
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = async response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    await notifyTx(order?.id);
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  try {
    const bodyParams = {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    };

    const queryParams = {
      include: ['booking', 'provider'],
      expand: true,
    };

    const response = await sdk.transactions.transition(bodyParams, queryParams);

    const order = response.data.data;
    dispatch(confirmPaymentSuccess(order.id));
    await SharetribeApi.createTxNotificationApi({ txId: transactionId });

    return order;
  } catch (e) {
    dispatch(confirmPaymentError(storableError(e)));
    const transactionIdMaybe =
      typeof transactionId === 'string'
        ? { transactionId }
        : { transactionId: transactionId?.uuid };
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
    });
    throw e;
  }
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(async response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      await notifyTx(transactionId);
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    priceVariantName,
    quantity,
    bookingDates,
    cartItems,
    size,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...(size ? { size } : {}),
    ...(cartItems ? { cartItems } : {}),
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const speculateTransactionWithPayPal = (
  params,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, merchantId, bookingDates, ...otherOrderParams } = params;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    deliveryMethod,
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    return PayPalApi.transitionPrivileged({
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return PayPalApi.initiateSpeculative({
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const initiateOrderWithPayPal = (orderParams, extraParams, transactionId) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateOrderRequest());

  const isTransition = !!transactionId;
  orderParams = withEmailDataInProtectedData(orderParams);
  const transition = isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL
    : transitions.REQUEST_PAYMENT_WITH_PAYPAL;

  const isPrivilegedTransition = isPrivileged(transition);

  const handleSucces = async response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    await notifyTx(order?.id || transactionId);
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    return PayPalApi.transitionPrivileged({
      isSpeculative: false,
      orderParams,
      extraParams,
    })
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return PayPalApi.initiatePrivileged({
      isSpeculative: false,
      orderParams,
      extraParams,
    })
      .then(handleSucces)
      .catch(handleError);
  }
};

export const createOrderIdWithPaypal = (orderParams, transactionId, processAlias) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateOrderRequest());
  orderParams = withEmailDataInProtectedData(orderParams);
  const isTransition = !!transactionId;
  const transition = isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL
    : transitions.REQUEST_PAYMENT_WITH_PAYPAL;

  const {
    deliveryMethod,
    quantity,
    bookingDates = {},
    merchantId,
    ...otherOrderParams
  } = orderParams;

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = { deliveryMethod };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? { id: transactionId, transition, params: transitionParams }
    : { processAlias, transition, params: transitionParams };

  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  const handleSuccess = async response => {
    if (response?.data) {
      const entities = denormalisedResponseEntities(response);
      const tx = entities?.[0];

      const txIdToNotify = tx?.id || transactionId;

      await notifyTx(txIdToNotify);
    }

    return response;
  };

  const handleError = e => {
    throw e;
  };

  return PayPalApi.getOrderId({
    merchantId,
    isSpeculative: false,
    orderData,
    bodyParams,
    queryParams,
  })
    .then(handleSuccess)
    .catch(handleError);
};

export const speculateTransactionWithWallet = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(typeof quantity !== 'undefined' ? { quantity } : {}),
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    return transitionPrivileged({
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return initiateWalletPrivileged({
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const initiateOrderWithWallet = (orderParams, extraParams, transactionId, processAlias) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateOrderRequest());
  const isTransition = !!transactionId;
  orderParams = withEmailDataInProtectedData(orderParams);
  const transition = isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_WALLET
    : transitions.REQUEST_PAYMENT_WITH_WALLET;

  const isPrivilegedTransition = isPrivileged(transition);

  const { deliveryMethod, quantity, bookingDates = {}, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(typeof quantity !== 'undefined' ? { quantity } : {}),
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    ...extraParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params: transitionParams,
      }
    : {
        processAlias,
        transition,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = async response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    await notifyTx(order?.id || transactionId);
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    return transitionPrivileged({
      isSpeculative: false,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    return initiateWalletPrivileged({
      isSpeculative: false,
      orderData,
      bodyParams,
      queryParams,
    })
      .then(handleSucces)
      .catch(handleError);
  }
};

export const createOrderIdWithWallet = (orderParams, transactionId, processAlias) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateOrderRequest());
  orderParams = withEmailDataInProtectedData(orderParams);
  const isTransition = !!transactionId;
  const transition = isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_WALLET
    : transitions.REQUEST_PAYMENT_WITH_WALLET;

  const { deliveryMethod, quantity, bookingDates = {}, ...otherOrderParams } = orderParams;

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(typeof quantity !== 'undefined' ? { quantity } : {}),
  };

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? { id: transactionId, transition, params: transitionParams }
    : { processAlias, transition, params: transitionParams };

  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  const handleSuccess = async response => {
    const entities = denormalisedResponseEntities(response);
    const tx = entities?.[0];

    const txIdToNotify = tx?.id || transactionId;

    await notifyTx(txIdToNotify);

    return response;
  };

  const handleError = e => {
    throw e;
  };

  return initiateWalletPrivileged({
    isSpeculative: false,
    orderData,
    bodyParams,
    queryParams,
  })
    .then(handleSuccess)
    .catch(handleError);
};

/**
 * Creates a PaymentIntent for Express Checkout (Apple Pay/Google Pay)
 * This must be called BEFORE stripe.confirmPayment() to get the clientSecret
 *
 * Uses the existing StripeApi.createExpressCheckoutPaymentIntent method
 *
 * @param {Object} params - Payment parameters
 * @param {number} params.amount - Amount in smallest currency unit (pence for GBP)
 * @param {string} params.currency - Currency code (e.g., 'gbp')
 * @param {Object} params.listingId - The listing ID
 * @param {string} params.listingTitle - Listing title for description
 * @param {string} params.providerId - The seller/provider user ID
 * @returns {Promise<{clientSecret: string, paymentIntentId: string}>}
 */
export const createPaymentIntent = params => async (dispatch, getState) => {
  dispatch(createPaymentIntentRequest());

  const { amount, currency = 'gbp', listingId, listingTitle, providerId } = params;

  // Get current user from state
  const state = getState();
  const currentUser = state.user?.currentUser;
  const currentUserId = currentUser?.id?.uuid;
  const userEmail = currentUser?.attributes?.email;
  const userFullName = currentUser?.attributes?.profile
    ? `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`
    : null;

  try {
    // Use the existing StripeApi method from api.js
    const response = await StripeApi.createExpressCheckoutPaymentIntent({
      amount,
      currency,
      currentUserId,
      providerId,
      listingId: listingId?.uuid || listingId,
      listingTitle,
      userEmail,
      userFullName,
    });

    // Handle the ApiResponse structure - response.data contains the actual data
    const result = response?.data || response;

    if (!result?.clientSecret) {
      throw new Error('No client secret returned from server');
    }

    dispatch(createPaymentIntentSuccess(result));

    return {
      clientSecret: result.clientSecret,
      paymentIntentId: result.paymentIntentId,
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    dispatch(createPaymentIntentError(storableError(error)));
    throw error;
  }
};
